import React, { useState } from 'react';
import {
  createBuilding, createUnit, createTenant, getUnits
} from '../services/api';

const NAVY = '#1f4e79';
const ORANGE = '#f26522';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; background: #f8fafc; }

  /* ── NAVBAR ── */
  .su-nav {
    position: sticky; top: 0; z-index: 100;
    height: 160px; padding: 0 6%;
    background: rgba(255,255,255,0.97);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(31,78,121,0.08);
    box-shadow: 0 4px 24px rgba(0,0,0,0.05);
    display: flex; align-items: center; justify-content: space-between;
  }
  .su-brand { display: flex; align-items: center; gap: 10px; text-decoration: none; }
  .su-brand img { height: 150px; width: auto; object-fit: contain; }
  .su-brand-pill {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 10px; font-weight: 700; letter-spacing: 1.5px;
    text-transform: uppercase; color: ${ORANGE};
    background: rgba(242,101,34,0.10);
    border: 1px solid rgba(242,101,34,0.25);
    padding: 3px 10px; border-radius: 20px;
  }
  .su-logout {
    font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 500;
    color: #64748b; background: none; cursor: pointer;
    padding: 8px 16px; border-radius: 8px;
    border: 1.5px solid #e2e8f0; transition: all 0.2s;
  }
  .su-logout:hover { background: #fef2f2; color: #dc2626; border-color: #fecaca; }

  /* ── PROGRESS BAR ── */
  .su-progress-wrap { background: #e2e8f0; height: 3px; }
  .su-progress-bar {
    background: linear-gradient(90deg, ${NAVY}, ${ORANGE});
    height: 3px; transition: width 0.5s cubic-bezier(0.4,0,0.2,1);
  }

  /* ── MAIN ── */
  .su-main {
    max-width: 600px; margin: 0 auto;
    padding: 48px 24px 100px;
  }

  /* ── STEP INDICATOR ── */
  .su-steps {
    display: flex; align-items: center; margin-bottom: 36px;
  }
  .su-step-item { display: flex; align-items: center; }
  .su-step-circle {
    width: 36px; height: 36px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px; font-weight: 700;
    transition: all 0.3s;
  }
  .su-step-circle.done { background: #22c55e; color: #fff; }
  .su-step-circle.active { background: ${NAVY}; color: #fff; box-shadow: 0 4px 12px rgba(31,78,121,0.30); }
  .su-step-circle.idle { background: #e2e8f0; color: #94a3b8; }
  .su-step-label {
    font-size: 12px; margin-left: 8px; white-space: nowrap;
    font-weight: 500;
  }
  .su-step-label.done { color: #22c55e; }
  .su-step-label.active { color: ${NAVY}; font-weight: 700; }
  .su-step-label.idle { color: #94a3b8; }
  .su-step-line {
    flex: 1; height: 2px; margin: 0 12px;
    transition: background 0.3s;
  }
  .su-step-line.done { background: #22c55e; }
  .su-step-line.idle { background: #e2e8f0; }

  /* ── HEADER ── */
  .su-header { margin-bottom: 28px; }
  .su-step-tag {
    display: inline-block; font-size: 11px; font-weight: 700;
    color: ${ORANGE}; text-transform: uppercase; letter-spacing: 2px;
    margin-bottom: 8px;
  }
  .su-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 26px; font-weight: 800; color: #0f172a;
    letter-spacing: -0.5px; margin-bottom: 6px;
  }
  .su-desc { font-size: 14px; color: #64748b; line-height: 1.6; }

  /* ── CARD ── */
  .su-card {
    background: #fff; border: 1px solid rgba(31,78,121,0.10);
    border-radius: 20px; padding: 32px;
    box-shadow: 0 4px 24px rgba(31,78,121,0.06);
  }

  /* ── FORM ELEMENTS ── */
  .su-field { margin-bottom: 20px; }
  .su-label {
    display: block; font-size: 13px; font-weight: 600;
    color: #374151; margin-bottom: 7px;
  }
  .su-label span { color: #94a3b8; font-weight: 400; font-size: 12px; }
  .su-input {
    width: 100%; padding: 13px 16px;
    border: 1.5px solid #e2e8f0; border-radius: 12px;
    font-family: 'Inter', sans-serif; font-size: 14px; color: #0f172a;
    background: #f8fafc; outline: none; transition: all 0.2s;
  }
  .su-input:focus {
    border-color: ${ORANGE}; background: #fff;
    box-shadow: 0 0 0 3px rgba(242,101,34,0.10);
  }
  .su-input::placeholder { color: #94a3b8; }
  .su-select {
    width: 100%; padding: 13px 16px;
    border: 1.5px solid #e2e8f0; border-radius: 12px;
    font-family: 'Inter', sans-serif; font-size: 14px; color: #0f172a;
    background: #f8fafc; outline: none; transition: all 0.2s;
    cursor: pointer; appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 14px center;
  }
  .su-select:focus { border-color: ${ORANGE}; background-color: #fff; box-shadow: 0 0 0 3px rgba(242,101,34,0.10); }
  .su-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

  /* ── BUTTONS ── */
  .btn-primary {
    width: 100%; padding: 15px;
    background: linear-gradient(135deg, ${ORANGE}, #e05a1a);
    color: #fff; border: none; cursor: pointer;
    border-radius: 12px; font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px; font-weight: 700;
    transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
    box-shadow: 0 4px 16px rgba(242,101,34,0.30);
    margin-bottom: 12px; margin-top: 8px;
  }
  .btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(242,101,34,0.40); }
  .btn-primary:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }

  .btn-success {
    width: 100%; padding: 15px;
    background: #f0fdf4; color: #166534;
    border: 1.5px solid #86efac; border-radius: 12px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px; font-weight: 700; cursor: pointer;
    transition: all 0.2s; margin-bottom: 12px;
  }
  .btn-success:hover { background: #dcfce7; }

  .btn-navy {
    width: 100%; padding: 15px;
    background: linear-gradient(135deg, ${NAVY}, #2d6fa8);
    color: #fff; border: none; cursor: pointer;
    border-radius: 12px; font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px; font-weight: 700;
    transition: all 0.25s; box-shadow: 0 4px 16px rgba(31,78,121,0.25);
    margin-bottom: 12px;
  }
  .btn-navy:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(31,78,121,0.35); }

  .btn-ghost {
    width: 100%; padding: 13px;
    background: #f8fafc; border: 1.5px solid #e2e8f0;
    color: #64748b; border-radius: 12px;
    font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 500;
    cursor: pointer; transition: all 0.2s;
  }
  .btn-ghost:hover { background: #f1f5f9; color: ${NAVY}; }

  /* ── ADDED ITEMS ── */
  .su-added-list { margin-bottom: 20px; }
  .su-added-title {
    font-size: 12px; font-weight: 700; color: #64748b;
    text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px;
  }
  .su-added-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 14px; background: #f0fdf4;
    border: 1px solid #bbf7d0; border-radius: 10px;
    margin-bottom: 8px; font-size: 14px; font-weight: 500; color: #166534;
  }
  .su-added-check {
    width: 20px; height: 20px; border-radius: 50%;
    background: #22c55e; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; flex-shrink: 0;
  }

  /* ── DIVIDER ── */
  .su-divider {
    border: none; border-top: 1px solid #f1f5f9; margin: 20px 0;
  }

  /* ── ERROR ── */
  .su-error {
    background: #fef2f2; border: 1px solid #fecaca;
    border-radius: 10px; padding: 12px 16px;
    color: #dc2626; font-size: 13px; margin-top: 12px; line-height: 1.5;
  }

  /* ── HINT ── */
  .su-hint {
    display: flex; align-items: flex-start; gap: 8px;
    background: rgba(31,78,121,0.04); border: 1px solid rgba(31,78,121,0.10);
    border-radius: 10px; padding: 12px 14px; margin-bottom: 20px;
  }
  .su-hint-icon { font-size: 16px; flex-shrink: 0; }
  .su-hint-text { font-size: 12px; color: #64748b; line-height: 1.6; }

  /* ── FOOTER NOTE ── */
  .su-footer-note {
    text-align: center; color: #94a3b8;
    font-size: 12px; margin-top: 20px; line-height: 1.6;
  }
`;

export default function Setup({ user, onComplete, onLogout }) {
  const [step, setStep] = useState(1);
  const [building, setBuilding] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1
  const [buildingName, setBuildingName] = useState('');
  const [buildingAddress, setBuildingAddress] = useState('');

  // Step 2
  const [unitNumber, setUnitNumber] = useState('');
  const [unitFloor, setUnitFloor] = useState('');
  const [unitRent, setUnitRent] = useState('');
  const [unitAdded, setUnitAdded] = useState(false);

  // Step 3
  const [selectedUnit, setSelectedUnit] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  const [tenantDeposit, setTenantDeposit] = useState('');
  const [tenantMoveIn, setTenantMoveIn] = useState('');
  const [tenantsAdded, setTenantsAdded] = useState([]);

  const progressPercent = ((step - 1) / 3) * 100;

  async function handleAddBuilding() {
    if (!buildingName.trim()) { setError('Enter building name'); return; }
    if (!buildingAddress.trim()) { setError('Enter building address'); return; }
    setLoading(true); setError('');
    try {
      const res = await createBuilding({ name: buildingName.trim(), address: buildingAddress.trim(), landlord_id: user.id });
      setBuilding(res.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save building');
    } finally { setLoading(false); }
  }

  async function handleAddUnit() {
    if (!unitNumber.trim()) { setError('Enter unit number'); return; }
    if (!unitRent || isNaN(unitRent)) { setError('Enter valid rent amount'); return; }
    setLoading(true); setError('');
    try {
      await createUnit({ unit_number: unitNumber.trim(), floor: unitFloor ? parseInt(unitFloor) : null, rent_amount: parseFloat(unitRent), building_id: building.id });
      const res = await getUnits(building.id);
      setUnits(res.data);
      setUnitNumber(''); setUnitFloor(''); setUnitRent('');
      setUnitAdded(true); setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save unit');
    } finally { setLoading(false); }
  }

  async function handleAddTenant() {
    if (!selectedUnit) { setError('Select a unit'); return; }
    if (!tenantName.trim()) { setError('Enter tenant name'); return; }
    if (tenantPhone.length !== 10) { setError('Enter valid 10-digit phone'); return; }
    setLoading(true); setError('');
    try {
      await createTenant({ name: tenantName.trim(), phone: tenantPhone, unit_id: parseInt(selectedUnit), deposit_amount: tenantDeposit ? parseFloat(tenantDeposit) : null, move_in_date: tenantMoveIn || null });
      setTenantsAdded([...tenantsAdded, tenantName.trim()]);
      setTenantName(''); setTenantPhone(''); setTenantDeposit(''); setTenantMoveIn(''); setSelectedUnit(''); setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save tenant');
    } finally { setLoading(false); }
  }

  const STEPS = [
    { n: 1, label: 'Building' },
    { n: 2, label: 'Units' },
    { n: 3, label: 'Tenants' },
  ];

  return (
    <div>
      <style>{styles}</style>

      {/* NAVBAR */}
      <nav className="su-nav">
        <a href="/" className="su-brand">
          <img src="/Hashvi_logo_no_BG.svg" alt="Hashvi Rental" />
          <span className="su-brand-pill">Rental</span>
        </a>
        <button className="su-logout" onClick={onLogout}>Logout</button>
      </nav>

      {/* PROGRESS BAR */}
      <div className="su-progress-wrap">
        <div className="su-progress-bar" style={{ width: progressPercent + '%' }} />
      </div>

      <div className="su-main">

        {/* STEP INDICATOR */}
        <div className="su-steps">
          {STEPS.map((s, i) => {
            const status = step > s.n ? 'done' : step === s.n ? 'active' : 'idle';
            return (
              <React.Fragment key={s.n}>
                <div className="su-step-item">
                  <div className={`su-step-circle ${status}`}>
                    {step > s.n ? '✓' : s.n}
                  </div>
                  <span className={`su-step-label ${status}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`su-step-line ${step > s.n ? 'done' : 'idle'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* STEP HEADER */}
        <div className="su-header">
          <div className="su-step-tag">Step {step} of 3</div>
          <div className="su-title">
            {step === 1 && 'Add Your Property 🏠'}
            {step === 2 && 'Add Your Units 🔑'}
            {step === 3 && 'Add Your Tenants 👥'}
          </div>
          <div className="su-desc">
            {step === 1 && 'Start by telling us about your property. You can add more buildings later from the dashboard.'}
            {step === 2 && `Adding units to ${building?.name}. Each unit gets its own rent amount and payment tracking.`}
            {step === 3 && 'Add tenants to your units. They\'ll receive a welcome message on WhatsApp automatically.'}
          </div>
        </div>

        {/* CARD */}
        <div className="su-card">

          {/* ── STEP 1 — BUILDING ── */}
          {step === 1 && (
            <>
              <div className="su-hint">
                <span className="su-hint-icon">💡</span>
                <span className="su-hint-text">
                  This could be your apartment building, house name, or any name you use to identify your property.
                </span>
              </div>

              <div className="su-field">
                <label className="su-label">Building / Property Name</label>
                <input
                  className="su-input"
                  type="text"
                  placeholder="e.g. Sunrise Apartments, My House"
                  value={buildingName}
                  onChange={e => setBuildingName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddBuilding()}
                />
              </div>

              <div className="su-field">
                <label className="su-label">Full Address</label>
                <input
                  className="su-input"
                  type="text"
                  placeholder="e.g. 12 MG Road, Whitefield, Bangalore 560066"
                  value={buildingAddress}
                  onChange={e => setBuildingAddress(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddBuilding()}
                />
              </div>

              {error && <div className="su-error">⚠️ {error}</div>}

              <button className="btn-primary" onClick={handleAddBuilding} disabled={loading}>
                {loading ? 'Saving...' : 'Save Property & Continue →'}
              </button>
            </>
          )}

          {/* ── STEP 2 — UNITS ── */}
          {step === 2 && (
            <>
              {units.length > 0 && (
                <div className="su-added-list">
                  <div className="su-added-title">Units added ({units.length})</div>
                  {units.map((u, i) => (
                    <div className="su-added-item" key={i}>
                      <span className="su-added-check">✓</span>
                      Unit {u.unit_number} — ₹{u.rent_amount.toLocaleString()}/month
                    </div>
                  ))}
                  <hr className="su-divider" />
                </div>
              )}

              <div className="su-hint">
                <span className="su-hint-icon">💡</span>
                <span className="su-hint-text">
                  Add each rentable unit — flat number, floor and monthly rent. You can add more units later from the dashboard.
                </span>
              </div>

              <div className="su-grid">
                <div className="su-field">
                  <label className="su-label">Unit Number</label>
                  <input
                    className="su-input"
                    type="text"
                    placeholder="e.g. 101, A2, GF"
                    value={unitNumber}
                    onChange={e => setUnitNumber(e.target.value)}
                  />
                </div>
                <div className="su-field">
                  <label className="su-label">Floor <span>(optional)</span></label>
                  <input
                    className="su-input"
                    type="number"
                    placeholder="e.g. 1, 2, 3"
                    value={unitFloor}
                    onChange={e => setUnitFloor(e.target.value)}
                  />
                </div>
              </div>

              <div className="su-field">
                <label className="su-label">Monthly Rent (₹)</label>
                <input
                  className="su-input"
                  type="number"
                  placeholder="e.g. 12000"
                  value={unitRent}
                  onChange={e => setUnitRent(e.target.value)}
                />
              </div>

              {error && <div className="su-error">⚠️ {error}</div>}

              <button className="btn-primary" onClick={handleAddUnit} disabled={loading}>
                {loading ? 'Adding...' : '+ Add Unit'}
              </button>

              {unitAdded && (
                <button className="btn-success" onClick={() => setStep(3)}>
                  Done Adding Units — Continue →
                </button>
              )}
            </>
          )}

          {/* ── STEP 3 — TENANTS ── */}
          {step === 3 && (
            <>
              {tenantsAdded.length > 0 && (
                <div className="su-added-list">
                  <div className="su-added-title">Tenants added ({tenantsAdded.length})</div>
                  {tenantsAdded.map((name, i) => (
                    <div className="su-added-item" key={i}>
                      <span className="su-added-check">✓</span>
                      {name} — Welcome WhatsApp sent ✓
                    </div>
                  ))}
                  <hr className="su-divider" />
                </div>
              )}

              <div className="su-hint">
                <span className="su-hint-icon">💬</span>
                <span className="su-hint-text">
                  When you add a tenant, they automatically receive a welcome message on WhatsApp with payment instructions.
                </span>
              </div>

              <div className="su-field">
                <label className="su-label">Select Unit</label>
                <select
                  className="su-select"
                  value={selectedUnit}
                  onChange={e => setSelectedUnit(e.target.value)}
                >
                  <option value="">Choose a unit</option>
                  {units.map(u => (
                    <option key={u.id} value={u.id}>
                      Unit {u.unit_number} — ₹{u.rent_amount.toLocaleString()}/month
                    </option>
                  ))}
                </select>
              </div>

              <div className="su-field">
                <label className="su-label">Tenant Full Name</label>
                <input
                  className="su-input"
                  type="text"
                  placeholder="As it should appear on receipts"
                  value={tenantName}
                  onChange={e => setTenantName(e.target.value)}
                />
              </div>

              <div className="su-field">
                <label className="su-label">WhatsApp Number</label>
                <input
                  className="su-input"
                  type="tel"
                  placeholder="10-digit number"
                  value={tenantPhone}
                  onChange={e => setTenantPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                />
              </div>

              <div className="su-grid">
                <div className="su-field">
                  <label className="su-label">Security Deposit (₹) <span>(optional)</span></label>
                  <input
                    className="su-input"
                    type="number"
                    placeholder="e.g. 25000"
                    value={tenantDeposit}
                    onChange={e => setTenantDeposit(e.target.value)}
                  />
                </div>
                <div className="su-field">
                  <label className="su-label">Move-in Date <span>(optional)</span></label>
                  <input
                    className="su-input"
                    type="date"
                    value={tenantMoveIn}
                    onChange={e => setTenantMoveIn(e.target.value)}
                  />
                </div>
              </div>

              {error && <div className="su-error">⚠️ {error}</div>}

              <button className="btn-primary" onClick={handleAddTenant} disabled={loading}>
                {loading ? 'Adding...' : '+ Add Tenant & Send WhatsApp Welcome'}
              </button>

              <hr className="su-divider" />

              {tenantsAdded.length > 0 ? (
                <button className="btn-navy" onClick={onComplete}>
                  Go to Dashboard →
                </button>
              ) : (
                <button className="btn-ghost" onClick={onComplete}>
                  Skip for now — Add tenants from dashboard
                </button>
              )}
            </>
          )}

        </div>

        <p className="su-footer-note">
          You can add more buildings, units and tenants anytime from your dashboard.
        </p>
      </div>
    </div>
  );
}
